<script>
import { useAlert } from 'dashboard/composables';
import { mapGetters } from 'vuex';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import ContextMenu from 'dashboard/components/ui/ContextMenu.vue';
import AddCannedModal from 'dashboard/routes/dashboard/settings/canned/AddCanned.vue';
import { useSnakeCase } from 'dashboard/composables/useTransformKeys';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import { conversationUrl, frontendURL } from '../../../helper/URLHelper';
import {
  ACCOUNT_EVENTS,
  CONVERSATION_EVENTS,
} from '../../../helper/AnalyticsHelper/events';
import MenuItem from '../../../components/widgets/conversation/contextMenu/menuItem.vue';
import { useTrack } from 'dashboard/composables';
import NextButton from 'dashboard/components-next/button/Button.vue';
import TelegramActionsAPI from 'dashboard/api/telegramActions';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

// Telegram's standard-account reaction set. Picking an unsupported emoji
// returns 400 from setMessageReaction, so we stick to the common ones that
// always work on standard (non-Premium-custom) reactions.
const TELEGRAM_REACTIONS = ['👍', '❤️', '🔥', '🥰', '👏', '😁', '🤔', '😱', '🙏', '👌'];

export default {
  components: {
    AddCannedModal,
    MenuItem,
    ContextMenu,
    NextButton,
  },
  props: {
    message: {
      type: Object,
      required: true,
    },
    isOpen: {
      type: Boolean,
      default: false,
    },
    enabledOptions: {
      type: Object,
      default: () => ({}),
    },
    contextMenuPosition: {
      type: Object,
      default: () => ({}),
    },
    hideButton: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['open', 'close', 'replyTo', 'telegramForward'],
  setup() {
    const { getPlainText } = useMessageFormatter();

    return {
      getPlainText,
    };
  },
  data() {
    return {
      isCannedResponseModalOpen: false,
      showDeleteModal: false,
      // Telegram action state
      telegramReactions: TELEGRAM_REACTIONS,
      showTgEmoji: false,
      tgBusy: false,
    };
  },
  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentAccountId: 'getCurrentAccountId',
      getUISettings: 'getUISettings',
    }),
    plainTextContent() {
      return this.getPlainText(this.messageContent);
    },
    conversationId() {
      return this.message.conversation_id ?? this.message.conversationId;
    },
    messageId() {
      return this.message.id;
    },
    messageContent() {
      return this.message.content;
    },
    contentAttributes() {
      return useSnakeCase(
        this.message.content_attributes ?? this.message.contentAttributes
      );
    },
  },
  methods: {
    async copyLinkToMessage() {
      const fullConversationURL =
        window.chatwootConfig.hostURL +
        frontendURL(
          conversationUrl({
            id: this.conversationId,
            accountId: this.currentAccountId,
          })
        );
      await copyTextToClipboard(
        `${fullConversationURL}?messageId=${this.messageId}`
      );
      useAlert(this.$t('CONVERSATION.CONTEXT_MENU.LINK_COPIED'));
      this.handleClose();
    },
    async handleCopy() {
      await copyTextToClipboard(this.plainTextContent);
      useAlert(this.$t('CONTACT_PANEL.COPY_SUCCESSFUL'));
      this.handleClose();
    },
    showCannedResponseModal() {
      useTrack(ACCOUNT_EVENTS.ADDED_TO_CANNED_RESPONSE);
      this.isCannedResponseModalOpen = true;
    },
    hideCannedResponseModal() {
      this.isCannedResponseModalOpen = false;
      this.handleClose();
    },
    handleOpen(e) {
      this.$emit('open', e);
    },
    handleClose(e) {
      this.$emit('close', e);
    },
    handleTranslate() {
      const { locale: accountLocale } = this.getAccount(this.currentAccountId);
      const agentLocale = this.getUISettings?.locale;
      const targetLanguage = agentLocale || accountLocale || 'en';
      this.$store.dispatch('translateMessage', {
        conversationId: this.conversationId,
        messageId: this.messageId,
        targetLanguage,
      });
      useTrack(CONVERSATION_EVENTS.TRANSLATE_A_MESSAGE);
      this.handleClose();
    },
    handleReplyTo() {
      this.$emit('replyTo', this.message);
      this.handleClose();
    },
    openDeleteModal() {
      this.handleClose();
      this.showDeleteModal = true;
    },
    // ---- Telegram-native message actions -------------------------------
    isTgPinned() {
      return this.contentAttributes?.is_pinned === true;
    },
    async tgReact(emoji) {
      if (this.tgBusy) return;
      this.tgBusy = true;
      try {
        const resp = await TelegramActionsAPI.reactToMessage(
          this.conversationId,
          this.messageId,
          emoji
        );
        if (resp.data && resp.data.ok === false) {
          useAlert(
            `Telegram refused the reaction: ${resp.data.error || 'unknown'}`
          );
        } else {
          // Optimistic Vuex update — the backend already persisted the
          // new content_attributes (with bot_reactions), but the open
          // conversation in Vuex still holds the stale copy. Pushing the
          // updated payload through Chatwoot's addOrUpdateMessage mutation
          // makes the reaction-pill appear immediately under the bubble.
          // Push the FULL refreshed message back into Vuex — ADD_MESSAGE
          // replaces the slot wholesale, so dispatching only a partial
          // payload would erase content/sender/status. Backend returns
          // message.push_event_data() exactly for this reason.
          if (resp.data?.message) {
            this.$store.dispatch('updateMessage', resp.data.message);
          }
        }
      } catch (e) {
        useAlert(`Reaction failed: ${e?.response?.data?.error || e.message}`);
      } finally {
        this.tgBusy = false;
        this.handleClose();
      }
    },
    async tgTogglePin() {
      if (this.tgBusy) return;
      this.tgBusy = true;
      const action = this.isTgPinned() ? 'unpin' : 'pin';
      try {
        const resp = await TelegramActionsAPI.pinMessage(
          this.conversationId,
          this.messageId,
          action
        );
        if (resp.data && resp.data.ok === false) {
          useAlert(`Pin failed: ${resp.data.error || 'unknown'}`);
        } else {
          useAlert(action === 'pin' ? 'Pinned in this chat.' : 'Pin removed.');
        }
      } catch (e) {
        useAlert(`Pin failed: ${e?.response?.data?.error || e.message}`);
      } finally {
        this.tgBusy = false;
        this.handleClose();
      }
    },
    tgEdit() {
      // Inline-composer edit (#173). Hand the message to FangateChatArea via
      // bus event — that component already owns the composer state, so it
      // can prefill the textarea with the existing content, show an Editing
      // banner, and call TelegramActionsAPI.editMessage on send. Closing the
      // context-menu before the bus event fires keeps the UI snappy.
      this.handleClose();
      this.$nextTick(() => {
        emitter.emit(BUS_EVENTS.START_TELEGRAM_EDIT, {
          id:                  this.messageId,
          conversation_id:     this.conversationId,
          content:             this.messageContent || '',
          content_attributes:  this.message.content_attributes,
        });
      });
    },
    async tgDelete() {
      // eslint-disable-next-line no-alert
      if (!window.confirm('Delete this message on Telegram for everyone?')) {
        return;
      }
      // OPTIMISTIC, Telegram-style: the message vanishes the instant you confirm
      // (displayedMessages drops content_attributes.deleted), the API runs in the
      // background, and we restore it only if Telegram refuses. No success toast —
      // a delete just disappears, exactly like the Telegram app.
      const snapshot = {
        ...this.message,
        content_attributes: { ...(this.message.content_attributes || {}) },
      };
      this.$store.dispatch('updateMessage', {
        ...snapshot,
        content_attributes: { ...snapshot.content_attributes, deleted: true },
      });
      this.handleClose();
      this.tgBusy = true;
      try {
        const resp = await TelegramActionsAPI.deleteMessage(
          this.conversationId,
          this.messageId
        );
        if (resp.data && resp.data.ok === false) {
          this.$store.dispatch('updateMessage', snapshot); // revert
          useAlert(`Delete failed: ${resp.data.error || 'unknown'}`);
        } else if (resp.data?.message) {
          // Sync the authoritative server copy (also deleted=true).
          this.$store.dispatch('updateMessage', resp.data.message);
        }
      } catch (e) {
        this.$store.dispatch('updateMessage', snapshot); // revert on network/server error
        useAlert(`Delete failed: ${e?.response?.data?.error || e.message}`);
      } finally {
        this.tgBusy = false;
      }
    },
    tgForward() {
      // Forward needs a target-conversation picker (which inbox/chat to send
      // to). That picker UI is a follow-up; for now we surface a clear note
      // instead of a dead click. The backend endpoint already exists
      // (TelegramActionsAPI.forwardMessage) and will wire in here.
      this.$emit('telegramForward', this.message);
      useAlert('Forward: choose a target chat — coming soon.');
      this.handleClose();
    },
    async confirmDeletion() {
      try {
        await this.$store.dispatch('deleteMessage', {
          conversationId: this.conversationId,
          messageId: this.messageId,
        });
        useAlert(this.$t('CONVERSATION.SUCCESS_DELETE_MESSAGE'));
        this.handleClose();
      } catch (error) {
        useAlert(this.$t('CONVERSATION.FAIL_DELETE_MESSSAGE'));
      }
    },
    closeDeleteModal() {
      this.showDeleteModal = false;
    },
  },
};
</script>

<template>
  <div class="context-menu">
    <!-- Add To Canned Responses -->
    <woot-modal
      v-if="isCannedResponseModalOpen && enabledOptions['cannedResponse']"
      v-model:show="isCannedResponseModalOpen"
      :on-close="hideCannedResponseModal"
    >
      <AddCannedModal
        :response-content="plainTextContent"
        :on-close="hideCannedResponseModal"
      />
    </woot-modal>
    <!-- Confirm Deletion -->
    <woot-delete-modal
      v-if="showDeleteModal && enabledOptions['delete']"
      v-model:show="showDeleteModal"
      class="context-menu--delete-modal"
      :on-close="closeDeleteModal"
      :on-confirm="confirmDeletion"
      :title="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.TITLE')"
      :message="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.MESSAGE')"
      :confirm-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.DELETE')"
      :reject-text="$t('CONVERSATION.CONTEXT_MENU.DELETE_CONFIRMATION.CANCEL')"
    />
    <NextButton
      v-if="!hideButton"
      ghost
      slate
      sm
      icon="i-lucide-ellipsis-vertical"
      class="invisible group-hover/context-menu:visible"
      @click="handleOpen"
    />
    <ContextMenu
      v-if="isOpen && !isCannedResponseModalOpen"
      :x="contextMenuPosition.x"
      :y="contextMenuPosition.y"
      @close="handleClose"
    >
      <div class="menu-container">
        <!-- ===== Telegram-native actions ===== -->
        <template v-if="enabledOptions['telegram']">
          <!-- Reaction emoji row.
               IMPORTANT: @mousedown.prevent on every emoji is mandatory.
               Chatwoot's ContextMenu wrapper (components/ui/ContextMenu.vue)
               has tabindex=0 + @blur=handleClose: any focusable child that
               receives focus on click (real <button> elements do, divs don't)
               triggers menu blur → handleClose → unmount BEFORE the click
               handler runs. That's why MenuItem (<div role=button>) Pin/Delete
               etc. work but raw <button> emoji clicks silently die.
               mousedown.prevent stops the default focus shift so the menu
               keeps focus and the click handler runs to completion. -->
          <div v-if="enabledOptions['telegramReact']" class="tg-emoji-row">
            <button
              v-for="e in telegramReactions"
              :key="e"
              class="tg-emoji-btn"
              :disabled="tgBusy"
              @mousedown.prevent
              @click.stop="tgReact(e)"
            >
              {{ e }}
            </button>
            <button
              class="tg-emoji-btn tg-emoji-clear"
              :disabled="tgBusy"
              title="Remove reaction"
              @mousedown.prevent
              @click.stop="tgReact('')"
            >
              ✕
            </button>
          </div>
          <MenuItem
            v-if="enabledOptions['telegramReply'] || enabledOptions['replyTo']"
            :option="{ icon: 'arrow-reply', label: 'Reply' }"
            variant="icon"
            @click.stop="handleReplyTo"
          />
          <MenuItem
            v-if="enabledOptions['telegramForward']"
            :option="{ icon: 'arrow-redo', label: 'Forward' }"
            variant="icon"
            @click.stop="tgForward"
          />
          <MenuItem
            v-if="enabledOptions['telegramPin']"
            :option="{ icon: 'location', label: isTgPinned() ? 'Unpin' : 'Pin' }"
            variant="icon"
            @click.stop="tgTogglePin"
          />
          <MenuItem
            v-if="enabledOptions['telegramEdit']"
            :option="{ icon: 'edit', label: 'Edit' }"
            variant="icon"
            @click.stop="tgEdit"
          />
          <MenuItem
            v-if="enabledOptions['copy']"
            :option="{
              icon: 'clipboard',
              label: $t('CONVERSATION.CONTEXT_MENU.COPY'),
            }"
            variant="icon"
            @click.stop="handleCopy"
          />
          <hr v-if="enabledOptions['telegramDelete']" />
          <MenuItem
            v-if="enabledOptions['telegramDelete']"
            :option="{ icon: 'delete', label: 'Delete' }"
            variant="icon"
            @click.stop="tgDelete"
          />
        </template>

        <!-- ===== Generic Chatwoot actions (non-Telegram inboxes) ===== -->
        <MenuItem
          v-if="!enabledOptions['telegram'] && enabledOptions['replyTo']"
          :option="{
            icon: 'arrow-reply',
            label: $t('CONVERSATION.CONTEXT_MENU.REPLY_TO'),
          }"
          variant="icon"
          @click.stop="handleReplyTo"
        />
        <MenuItem
          v-if="!enabledOptions['telegram'] && enabledOptions['copy']"
          :option="{
            icon: 'clipboard',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY'),
          }"
          variant="icon"
          @click.stop="handleCopy"
        />
        <MenuItem
          v-if="!enabledOptions['telegram'] && enabledOptions['translate']"
          :option="{
            icon: 'translate',
            label: $t('CONVERSATION.CONTEXT_MENU.TRANSLATE'),
          }"
          variant="icon"
          @click.stop="handleTranslate"
        />
        <hr v-if="!enabledOptions['telegram']" />
        <MenuItem
          v-if="!enabledOptions['telegram'] && enabledOptions['copyLink']"
          :option="{
            icon: 'link',
            label: $t('CONVERSATION.CONTEXT_MENU.COPY_PERMALINK'),
          }"
          variant="icon"
          @click.stop="copyLinkToMessage"
        />
        <MenuItem
          v-if="!enabledOptions['telegram'] && enabledOptions['cannedResponse']"
          :option="{
            icon: 'comment-add',
            label: $t('CONVERSATION.CONTEXT_MENU.CREATE_A_CANNED_RESPONSE'),
          }"
          variant="icon"
          @click.stop="showCannedResponseModal"
        />
        <hr v-if="!enabledOptions['telegram'] && enabledOptions['delete']" />
        <MenuItem
          v-if="!enabledOptions['telegram'] && enabledOptions['delete']"
          :option="{
            icon: 'delete',
            label: $t('CONVERSATION.CONTEXT_MENU.DELETE'),
          }"
          variant="icon"
          @click.stop="openDeleteModal"
        />
      </div>
    </ContextMenu>
  </div>
</template>

<style lang="scss" scoped>
// Telegram quick-reaction emoji row at the top of the menu.
.tg-emoji-row {
  @apply flex flex-wrap items-center gap-0.5 px-1 pt-1 pb-1.5 max-w-[230px];
}
.tg-emoji-btn {
  @apply w-7 h-7 flex items-center justify-center rounded text-base
         hover:bg-n-alpha-2 disabled:opacity-50 disabled:cursor-progress;
}
.tg-emoji-clear {
  @apply text-xs text-n-slate-11;
}

.menu-container {
  @apply p-1 bg-n-background shadow-xl rounded-md;

  hr:first-child {
    @apply hidden;
  }

  hr {
    @apply m-1 border-b border-solid border-n-strong;
  }
}

.context-menu--delete-modal {
  ::v-deep {
    .modal-container {
      @apply max-w-[30rem];

      h2 {
        @apply font-medium text-base;
      }
    }
  }
}
</style>
